import { useEffect, useState } from "react";
import { Bluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";

function runDiagnostics() {
    const start = Date.now();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);

    return fetch(`${Bluelagoon.apiUrl}/`, {
        method: "HEAD",
        cache: "no-store",
        signal: controller.signal,
    })
        .then((res) => {
            clearTimeout(timeout);
            const latency = Date.now() - start;
            return {
                ok: true,
                status: res.status,
                latency,
                dns: true,
                api: true,
                error: null,
            };
        })
        .catch((err) => {
            clearTimeout(timeout);
            const latency = Date.now() - start;
            const isTimeout = err.name === "AbortError";
            const isNetworkFailure = err instanceof TypeError;

            return {
                ok: false,
                status: null,
                latency,
                dns: !isNetworkFailure,
                api: false,
                error: isTimeout
                    ? "TIMEOUT"
                    : isNetworkFailure
                      ? "NETWORK_ERROR"
                      : `UNKNOWN: ${err.message}`,
            };
        });
}

function ErrorMessage({ error, t }) {
    if (error === "TIMEOUT") return t("connectivity.errorTimeout");
    if (error === "NETWORK_ERROR") return t("connectivity.errorNetwork");
    return t("connectivity.errorUnknown");
}

function ConnectivityCheck() {
    const { t } = useTranslation();
    const [result, setResult] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const [loading, setLoading] = useState(false);

    const check = () => {
        setLoading(true);
        runDiagnostics().then((r) => {
            setResult(r.ok ? null : r);
            setLoading(false);
        });
    };

    useEffect(check, []);

    if (!result || dismissed) return null;

    return (
        <div className="fixed bottom-4 right-4 z-50 w-96 rounded-lg border border-destructive/50 bg-background p-4 shadow-lg">
            <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-destructive">
                    {t("connectivity.title")}
                </h3>
                <button
                    onClick={() => setDismissed(true)}
                    className="text-muted-foreground hover:text-foreground"
                >
                    &times;
                </button>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
                {t("connectivity.description")}
            </p>
            <div className="rounded bg-muted/50 p-2 text-xs">
                <p className="font-medium text-destructive">
                    <ErrorMessage error={result.error} t={t} />
                </p>
                <p className="mt-1 text-muted-foreground">
                    {t("connectivity.latency")}: {result.latency}ms
                </p>
            </div>
            <button
                onClick={check}
                disabled={loading}
                className="mt-3 w-full rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
                {loading
                    ? t("connectivity.checking")
                    : t("connectivity.retry")}
            </button>
        </div>
    );
}

export default ConnectivityCheck;
